import {
  LayoutGrid, Columns3, AlignLeft, Code2, BookOpen, Newspaper,
  Tags, Image, User, PanelRight, Gamepad2, Heart, Briefcase,
  Plane, Utensils, ShoppingBag, Mic, GraduationCap, Radio
} from "lucide-react";

export type TemplateVariable = {
  id: string;
  label: string;
  type: "categories" | "cover" | "author" | "widgets";
  icon: any;
  description: string;
  defaults: string[];
};

export type TemplateModel = {
  id: string;
  name: string;
  type: "broadsheet" | "broadcast" | "magazine" | "digital" | "niche";
  tagline: string;
  description: string;
  icon: any;
  previewColors: string[];
  inspirations: string[];
  layout: string;
  typography: string;
  features: string[];
  variables: TemplateVariable[];
};

const v = (cats: string[], hero: string[], author: string[], widgets: string[]): TemplateVariable[] => [
  { id: "categories", label: "Categories", type: "categories", icon: Tags, description: "Primary sections for navigation & filtering", defaults: cats },
  { id: "hero", label: "Cover / Hero", type: "cover", icon: Image, description: "Featured image and hero banner configuration", defaults: hero },
  { id: "author", label: "Author / Byline", type: "author", icon: User, description: "Writer identity, bio, and social links", defaults: author },
  { id: "widgets", label: "Sidebar Widgets", type: "widgets", icon: PanelRight, description: "Configurable sidebar and footer modules", defaults: widgets },
];

export const templateModels: TemplateModel[] = [
  // ═══════════════════════════════════════════
  // BROADSHEET — Classic newspaper DNA
  // ═══════════════════════════════════════════
  {
    id: "the-record",
    name: "The Record",
    type: "broadsheet",
    tagline: "Paper of Record",
    inspirations: ["The New York Times", "The Washington Post"],
    description: "Authoritative broadsheet with serif-dominant hierarchy, 4-column front page, above/below-the-fold architecture, and dateline masthead. Built for trust and depth.",
    icon: Newspaper,
    previewColors: ["#000000", "#FFFFFF", "#F7F5F2", "#567B95"],
    layout: "4-Column Broadsheet",
    typography: "Cheltenham + Imperial",
    features: ["Masthead with dateline", "Above-the-fold hero", "4-column grid", "Section jumps", "Newsletter embed", "Subscriber paywall", "Opinion columns", "Live updates badge"],
    variables: v(
      ["World", "U.S.", "Politics", "Business", "Tech", "Science", "Health", "Arts", "Opinion", "Sports"],
      ["16:9 lead photo", "Caption + credit", "Above-fold placement", "Breaking banner"],
      ["Byline", "Headshot", "Bureau", "Twitter/X", "Email"],
      ["Most Popular", "Editor's Picks", "Newsletters", "Subscriber-Only", "Live Briefing"]
    ),
  },
  {
    id: "le-quotidien",
    name: "Le Quotidien",
    type: "broadsheet",
    tagline: "Quotidien de Référence",
    inspirations: ["Le Figaro", "Le Monde", "La Repubblica"],
    description: "Élégant quotidien européen avec typographie serif classique, rubriques culturelles fortes, et mise en page éditoriale à la française. Support RTL natif.",
    icon: Newspaper,
    previewColors: ["#1B3A5C", "#FDFCF9", "#E8E2D8", "#C8102E"],
    layout: "European Broadsheet",
    typography: "Didot + Garamond Premier",
    features: ["Masthead à la française", "Rubrique culture proéminente", "Chroniques éditorialistes", "Cahier économie", "Bloc débats", "Support multilingue", "Edition PDF", "Archives"],
    variables: v(
      ["À la Une", "International", "Politique", "Économie", "Culture", "Sciences", "Sport", "Débats", "Cahier Weekend"],
      ["Photo une pleine largeur", "Ratio 3:2", "Crédit photographe", "Manchette breaking"],
      ["Signature", "Photo columnist", "Titre", "Rubrique attitrée"],
      ["Les Plus Lus", "Éditorial du Jour", "Newsletters", "Abonnement", "Chroniques"]
    ),
  },
  {
    id: "the-telegraph",
    name: "The Standard",
    type: "broadsheet",
    tagline: "National Daily",
    inspirations: ["The Telegraph", "The Guardian", "The Globe and Mail"],
    description: "Bold national broadsheet with strong section identities, compact+ format, data visualization integration, and investigative reporting showcases.",
    icon: Newspaper,
    previewColors: ["#0A1628", "#FFFFFF", "#F0F0F0", "#00543E"],
    layout: "Compact Broadsheet",
    typography: "Guardian Egyptian + Guardian Sans",
    features: ["Section color coding", "Data viz embeds", "Compact+ format", "Long-read labels", "Key points summary", "Comment threading", "Morning briefing", "Corrections log"],
    variables: v(
      ["News", "World", "Comment", "Business", "Culture", "Sport", "Money", "Travel", "Environment"],
      ["Lead image 16:9", "Key points box", "Data chart embed", "Photo essay strip"],
      ["Reporter", "Photo", "Specialism", "Articles count", "Contact"],
      ["Today's Paper", "Morning Briefing", "Podcasts", "Newsletters", "Data Explorer"]
    ),
  },
  {
    id: "al-sharq",
    name: "Al-Sharq",
    type: "broadsheet",
    tagline: "الصحيفة العربية",
    inspirations: ["Al Jazeera", "Al-Ahram", "Asharq Al-Awsat"],
    description: "RTL-native Arabic broadsheet with bi-directional layout engine, Islamic geometric accents, multilingual switching, and region-aware content delivery.",
    icon: Newspaper,
    previewColors: ["#B8860B", "#0C1620", "#F5F0E8", "#1B6B3A"],
    layout: "RTL Broadsheet",
    typography: "Noto Naskh Arabic + Amiri",
    features: ["Full RTL support", "Bi-directional layout", "Arabic calligraphy headers", "Hijri date", "Region switcher", "Prayer times widget", "Multi-script rendering", "Wire service feed"],
    variables: v(
      ["أخبار عاجلة", "سياسة", "اقتصاد", "رياضة", "ثقافة", "تكنولوجيا", "رأي", "عالمي"],
      ["صورة رئيسية", "بانر عاجل", "تغطية مباشرة", "معرض صور"],
      ["كاتب", "صورة", "التخصص", "تويتر"],
      ["الأكثر قراءة", "نشرة إخبارية", "بودكاست", "مواقيت الصلاة"]
    ),
  },

  // ═══════════════════════════════════════════
  // BROADCAST — TV/Digital-first news
  // ═══════════════════════════════════════════
  {
    id: "live-wire",
    name: "LiveWire",
    type: "broadcast",
    tagline: "24/7 Live News",
    inspirations: ["CNN", "BBC News", "Sky News"],
    description: "Always-on broadcast news hub with live video player, scrolling ticker, segment-based navigation, and real-time alert system. Built for speed and breaking coverage.",
    icon: Radio,
    previewColors: ["#CC0000", "#111111", "#1A1A1A", "#FFFFFF"],
    layout: "Broadcast Hub",
    typography: "CNN Sans + Helvetica Neue",
    features: ["Live video hero", "Breaking news ticker", "Alert push system", "Segment navigation", "Correspondent map", "Watch live button", "Clip replay", "Share moments"],
    variables: v(
      ["Breaking", "U.S.", "World", "Politics", "Business", "Health", "Entertainment", "Weather", "Sports"],
      ["Video player 16:9", "Live badge", "Breaking banner", "Correspondent standup"],
      ["Correspondent", "Bureau location", "Beat", "Live indicator"],
      ["Watch Live", "Breaking Alerts", "Top Stories", "Trending", "Program Schedule"]
    ),
  },
  {
    id: "info-24",
    name: "Info 24",
    type: "broadcast",
    tagline: "Chaîne Info Continue",
    inspirations: ["BFM TV", "France 24", "LCI"],
    description: "Chaîne info continue digitale avec flux vidéo en direct, bandeau défilant, flash info, et intégration réseaux sociaux. Optimisé pour le temps réel.",
    icon: Radio,
    previewColors: ["#0055A4", "#0A0A0A", "#1C1C1C", "#FF6B00"],
    layout: "Live Channel",
    typography: "Proxima Nova + Source Sans",
    features: ["Flux vidéo direct", "Bandeau défilant", "Flash info", "Multi-écrans", "Réactions RS", "Replay segments", "Alerte push", "Météo intégrée"],
    variables: v(
      ["Direct", "Flash", "Politique", "Économie", "International", "Société", "Sport", "Météo"],
      ["Player vidéo", "Badge EN DIRECT", "Bandeau urgent", "Split-screen"],
      ["Journaliste", "Plateau", "Édition", "En direct depuis"],
      ["En Direct", "Replay", "Flash Info", "Météo", "Grille des Programmes"]
    ),
  },
  {
    id: "pulse-digital",
    name: "Pulse",
    type: "broadcast",
    tagline: "Digital-First News",
    inspirations: ["Vice News", "Axios", "The Daily Beast"],
    description: "Punchy, mobile-first digital newsroom with card-based stories, smart brevity format, newsletter-native design, and engagement-first architecture.",
    icon: Radio,
    previewColors: ["#6D28D9", "#09090B", "#18181B", "#F472B6"],
    layout: "Card Feed",
    typography: "Inter + Noto Serif Display",
    features: ["Smart brevity cards", "Why it matters box", "Go deeper links", "Engagement meters", "Newsletter signup inline", "Social-native sharing", "Audio summary", "Infinite scroll"],
    variables: v(
      ["Big Stories", "Tech", "Politics", "Business", "Media", "World", "Deals", "Science"],
      ["Card image 1:1", "Gradient overlay", "Category badge", "Duration tag"],
      ["Reporter", "Handle", "Newsletter", "Followers"],
      ["What's Trending", "Newsletters", "Podcast Episodes", "Behind the Story"]
    ),
  },

  // ═══════════════════════════════════════════
  // MAGAZINE — Editorial prestige
  // ═══════════════════════════════════════════
  {
    id: "haute-editorial",
    name: "Haute",
    type: "magazine",
    tagline: "Fashion & Culture",
    inspirations: ["Vogue", "Harper's Bazaar", "W Magazine"],
    description: "Ultra-premium fashion editorial with full-bleed photography, dramatic typography, lookbook galleries, and immersive scrollytelling. Pure visual luxury.",
    icon: BookOpen,
    previewColors: ["#000000", "#FAF5F0", "#C4A77D", "#8B0000"],
    layout: "Full-Bleed Editorial",
    typography: "Bodoni Moda + Futura",
    features: ["Full-bleed hero imagery", "Lookbook galleries", "Scrollytelling", "Video lookbooks", "Issue archives", "Fashion week coverage", "Shopping integration", "Print-digital hybrid"],
    variables: v(
      ["Fashion", "Beauty", "Culture", "Living", "Runway", "Street Style", "Interviews", "Shopping"],
      ["Full-bleed portrait", "Parallax hero", "Cover shoot", "Editorial spread"],
      ["Photographer", "Stylist", "Writer", "Creative Director"],
      ["Editor's Letter", "Shop the Look", "Issue Archive", "Beauty Awards", "Fashion Week"]
    ),
  },
  {
    id: "the-atlantic",
    name: "The Review",
    type: "magazine",
    tagline: "Ideas & Deep Reporting",
    inspirations: ["The Atlantic", "The New Yorker", "Harper's Magazine"],
    description: "Intellectual magazine for longform journalism, essays, and cultural criticism. Chapter navigation, illustration-first covers, and serif-heavy reading experience.",
    icon: BookOpen,
    previewColors: ["#B91C1C", "#FBF8F4", "#1C1917", "#D4A574"],
    layout: "Longform Editorial",
    typography: "Mercury + Founders Grotesk",
    features: ["Chapter navigation", "Illustration covers", "Pull quotes", "Footnotes", "Series collections", "Print-quality layout", "Subscriber content", "Audio read-along"],
    variables: v(
      ["Ideas", "Politics", "Culture", "Technology", "Books", "Family", "Education", "Photo Essays", "Dispatches"],
      ["Illustrated cover", "4:5 ratio", "Caption block", "Series header"],
      ["Author portrait", "Long bio", "Books authored", "Awards"],
      ["Most Popular", "The Daily", "Magazine Issue", "Subscriber Exclusive", "Letters"]
    ),
  },
  {
    id: "mono-design",
    name: "Mono",
    type: "magazine",
    tagline: "Design & Architecture",
    inspirations: ["Monocle", "Wallpaper*", "Dezeen"],
    description: "Refined design magazine with Swiss-grid precision, clean photography, index-style navigation, and premium brand partnerships. For design-literate audiences.",
    icon: BookOpen,
    previewColors: ["#18181B", "#FAFAFA", "#A1A1AA", "#D4AF37"],
    layout: "Swiss Grid",
    typography: "Helvetica Now + Plantin",
    features: ["Swiss grid system", "Index navigation", "Photo essays", "City guides", "Design awards", "Brand partnerships", "Print shop", "Event listings"],
    variables: v(
      ["Design", "Architecture", "Travel", "Fashion", "Urbanism", "Gastronomy", "Business", "Affairs"],
      ["Clean 3:2 photo", "Grid placement", "Caption bar", "Index number"],
      ["Contributor", "City", "Title", "Issue credits"],
      ["The Forecast", "City Guides", "Shop", "Events", "Quality of Life Survey"]
    ),
  },
  {
    id: "culture-vulture",
    name: "Vulture",
    type: "magazine",
    tagline: "Pop Culture & Entertainment",
    inspirations: ["Vulture", "Pitchfork", "Dazed"],
    description: "Bold, high-energy culture magazine with ranked lists, review scores, embedded media players, and social-first sharing. For music, film, TV, and games.",
    icon: BookOpen,
    previewColors: ["#E11D48", "#0A0A0A", "#1A1A1A", "#FACC15"],
    layout: "Media-Rich Grid",
    typography: "Druk Wide + Atlas Grotesk",
    features: ["Review scores", "Ranked lists", "Embedded players", "Trailer previews", "Release calendars", "Critic consensus", "Social reactions", "Merch integration"],
    variables: v(
      ["TV", "Movies", "Music", "Books", "Games", "Comedy", "Theater", "Art", "Rankings"],
      ["Media poster", "Video embed", "Rating badge", "Playlist cover"],
      ["Critic", "Beat", "Rating history", "Social"],
      ["Best of Lists", "Reviews Today", "Trailers", "Calendar", "Critics' Picks"]
    ),
  },

  // ═══════════════════════════════════════════
  // DIGITAL — Tech, dev & startup native
  // ═══════════════════════════════════════════
  {
    id: "the-verge",
    name: "Vertex",
    type: "digital",
    tagline: "Tech & Science",
    inspirations: ["The Verge", "Wired", "Ars Technica"],
    description: "Bold tech publication with product reviews, benchmark tables, science reporting, and strong visual identity. Large imagery with opinionated editorial voice.",
    icon: Code2,
    previewColors: ["#6D28D9", "#0F0F0F", "#1A1A1A", "#22D3EE"],
    layout: "Visual Tech Grid",
    typography: "Manrope + iA Writer Duo",
    features: ["Product reviews with scores", "Benchmark comparisons", "Spec tables", "Hot takes bar", "Storystream timeline", "Science section", "Buying guides", "Comment threads"],
    variables: v(
      ["Tech", "Science", "Reviews", "Creators", "Deals", "Cars", "AI", "Policy", "Entertainment"],
      ["Product hero shot", "Comparison grid", "Score badge", "Video review"],
      ["Reviewer", "Expertise", "Review count", "Verdict"],
      ["Vergecast", "Hot Takes", "Buying Guides", "Storystreams", "Deals"]
    ),
  },
  {
    id: "dev-chronicle",
    name: "DevChronicle",
    type: "digital",
    tagline: "Developer Publication",
    inspirations: ["dev.to", "Hashnode", "Smashing Magazine"],
    description: "Dark-first developer publication with syntax highlighting, code playgrounds, tag-driven discovery, GitHub integration, and community contributions.",
    icon: Code2,
    previewColors: ["#10B981", "#0F172A", "#1E293B", "#38BDF8"],
    layout: "Dev Feed",
    typography: "JetBrains Mono + Inter",
    features: ["Syntax highlighting", "Live code playground", "Tag cloud", "Reading time", "GitHub sync", "Series collections", "Community reactions", "Dev newsletter"],
    variables: v(
      ["JavaScript", "Python", "Rust", "DevOps", "Cloud", "AI/ML", "Security", "Web3", "Career"],
      ["Code screenshot OG", "Terminal preview", "Diagram embed", "Auto-generated banner"],
      ["GitHub handle", "Tech stack", "Contributions", "Streak badge"],
      ["Trending Tags", "This Week", "GitHub Repos", "Job Board", "Sponsor Spotlight"]
    ),
  },
  {
    id: "startup-brief",
    name: "LaunchPad",
    type: "digital",
    tagline: "Startups & Venture",
    inspirations: ["TechCrunch", "The Information", "Crunchbase News"],
    description: "Startup and venture capital publication with funding round trackers, company profiles, investor databases, and exclusive deal flow coverage.",
    icon: Code2,
    previewColors: ["#16A34A", "#0C0A09", "#1C1917", "#FBBF24"],
    layout: "Deal Flow Feed",
    typography: "Plus Jakarta Sans + Literata",
    features: ["Funding round tracker", "Company profiles", "Investor database", "Valuation charts", "M&A timeline", "Unicorn list", "Pitch deck library", "Event calendar"],
    variables: v(
      ["Funding", "Startups", "AI", "FinTech", "HealthTech", "Crypto", "DeepTech", "Exits", "Analysis"],
      ["Company logo hero", "Funding chart", "Founder photo", "Product screenshot"],
      ["Reporter", "Beat", "Scoops count", "Newsletter"],
      ["Latest Rounds", "Unicorn Tracker", "Exits This Month", "Events", "Pitch Deck Library"]
    ),
  },
  {
    id: "secops-wire",
    name: "SecOps Wire",
    type: "digital",
    tagline: "Cybersecurity Intel",
    inspirations: ["Krebs on Security", "Bleeping Computer", "Dark Reading"],
    description: "Terminal-aesthetic cybersecurity publication with CVE tracking, threat timelines, IOC feeds, severity dashboards, and MITRE ATT&CK mapping.",
    icon: Code2,
    previewColors: ["#22C55E", "#020617", "#0F172A", "#EF4444"],
    layout: "Threat Intelligence Feed",
    typography: "JetBrains Mono + Space Grotesk",
    features: ["CVE tracker", "Severity badges", "IOC feed", "MITRE mapping", "Threat timeline", "Indicator search", "Advisory alerts", "Dark web monitoring"],
    variables: v(
      ["Threats", "Vulnerabilities", "Malware", "Breaches", "Research", "Nation-State", "Ransomware", "Zero-Day"],
      ["Threat banner", "Severity matrix", "Timeline header", "Attack flow diagram"],
      ["Researcher", "Affiliation", "CVEs credited", "PGP key"],
      ["Active Threats", "CVE Feed", "IOC Search", "Advisory Board", "Threat Map"]
    ),
  },

  // ═══════════════════════════════════════════
  // NICHE — Vertical-specific publications
  // ═══════════════════════════════════════════
  {
    id: "market-terminal",
    name: "Terminal",
    type: "niche",
    tagline: "Markets & Finance",
    inspirations: ["Bloomberg", "Financial Times", "Reuters"],
    description: "Bloomberg-grade financial publication with live market tickers, earnings dashboards, portfolio tracking, and analyst commentary. Data-dense, information-rich.",
    icon: Briefcase,
    previewColors: ["#F97316", "#000000", "#0F0F0F", "#22D3EE"],
    layout: "Data Terminal",
    typography: "IBM Plex Sans + IBM Plex Serif",
    features: ["Live market tickers", "Earnings calendar", "Portfolio tracker", "Chart embeds", "Analyst ratings", "Economic indicators", "Commodities tracker", "FX rates"],
    variables: v(
      ["Markets", "Stocks", "Bonds", "Crypto", "Commodities", "FX", "Economy", "Opinion", "Wealth"],
      ["Market chart hero", "Ticker strip", "Heat map", "Economic indicator"],
      ["Analyst", "Firm", "Track record", "Sectors covered"],
      ["Market Overview", "Watchlist", "Earnings", "Economic Calendar", "Portfolio"]
    ),
  },
  {
    id: "wanderlust-atlas",
    name: "Atlas",
    type: "niche",
    tagline: "Travel & Exploration",
    inspirations: ["Condé Nast Traveler", "National Geographic Traveler", "Lonely Planet"],
    description: "Immersive travel publication with full-screen destination heroes, interactive maps, itinerary builders, and photo journals. Wanderlust as editorial identity.",
    icon: Plane,
    previewColors: ["#0D9488", "#0C0A09", "#F0FDFA", "#F59E0B"],
    layout: "Destination Grid",
    typography: "Outfit + Libre Baskerville",
    features: ["Interactive maps", "Photo journals", "Itinerary builder", "Destination ratings", "Packing lists", "Flight price tracker", "Hotel reviews", "Visa guide"],
    variables: v(
      ["Destinations", "City Guides", "Adventure", "Luxury", "Budget", "Food & Drink", "Hotels", "Flights"],
      ["Destination hero", "Full-bleed landscape", "Map overlay", "Carousel gallery"],
      ["Traveler", "Countries visited", "Instagram", "Guides written"],
      ["Top Destinations", "Flight Deals", "Travel Gear", "Trip Planner", "Visa Info"]
    ),
  },
  {
    id: "saveur-table",
    name: "La Table",
    type: "niche",
    tagline: "Gastronomie & Recettes",
    inspirations: ["Bon Appétit", "Saveur", "Eater"],
    description: "Food publication with recipe cards, step-by-step photography, ingredient scaling, nutrition labels, and seasonal collections. Editorial meets cookbook.",
    icon: Utensils,
    previewColors: ["#EA580C", "#1C1917", "#FFF7ED", "#65A30D"],
    layout: "Recipe Magazine",
    typography: "DM Serif Display + Nunito Sans",
    features: ["Recipe cards", "Ingredient scaling", "Step-by-step photos", "Nutrition labels", "Print recipe", "Meal planner", "Shopping list", "Video tutorials"],
    variables: v(
      ["Recipes", "Restaurants", "Wine", "Technique", "Seasonal", "Quick Meals", "Baking", "World Cuisine"],
      ["Food hero shot", "Overhead 1:1", "Step gallery", "Plating detail"],
      ["Chef", "Restaurant", "Cookbook", "Michelin stars"],
      ["Season's Best", "Meal Planner", "Shopping List", "Collections", "Video Kitchen"]
    ),
  },
  {
    id: "game-nexus",
    name: "Nexus",
    type: "niche",
    tagline: "Gaming & Esports",
    inspirations: ["IGN", "Polygon", "Kotaku"],
    description: "Gaming publication with review scores, trailer embeds, release calendars, platform filters, wiki-style game databases, and esports tournament coverage.",
    icon: Gamepad2,
    previewColors: ["#A855F7", "#09090B", "#18181B", "#EAB308"],
    layout: "Gaming Hub",
    typography: "Exo 2 + Inter",
    features: ["Review scores /10", "Trailer embeds", "Release calendar", "Platform filters", "Wiki database", "Esports brackets", "Deal tracker", "Community forums"],
    variables: v(
      ["Reviews", "News", "Previews", "Guides", "Esports", "Indie", "PC", "Console", "Mobile"],
      ["Game art hero", "16:9 trailer", "Platform badges", "Score overlay"],
      ["Reviewer", "Gamertag", "Platform", "Hours played", "Completion %"],
      ["Upcoming", "Top Rated", "Deals", "Esports Schedule", "Community Picks"]
    ),
  },
  {
    id: "wellness-bloom",
    name: "Bloom",
    type: "niche",
    tagline: "Health & Wellness",
    inspirations: ["Well+Good", "Healthline", "Goop"],
    description: "Organic wellness publication with soft gradients, recipe cards, workout plans, mindfulness content, and expert-backed health reporting.",
    icon: Heart,
    previewColors: ["#84CC16", "#1C1917", "#FEFCE8", "#FB923C"],
    layout: "Wellness Grid",
    typography: "Nunito + Merriweather",
    features: ["Recipe cards", "Workout plans", "Meditation timer", "Progress tracker", "Expert Q&A", "Nutrition calculator", "Sleep tracker", "Community challenges"],
    variables: v(
      ["Nutrition", "Fitness", "Mental Health", "Recipes", "Yoga", "Sleep", "Skin Care", "Expert Advice"],
      ["Soft gradient hero", "Food photography", "Nature imagery", "Illustration"],
      ["Expert", "Certifications", "Specialty", "Publications"],
      ["Weekly Plan", "Recipe Box", "Guided Meditation", "Community", "Ask an Expert"]
    ),
  },
];

export const templateTypes = [
  { id: "all", label: "All" },
  { id: "broadsheet", label: "Broadsheet" },
  { id: "broadcast", label: "Broadcast" },
  { id: "magazine", label: "Magazine" },
  { id: "digital", label: "Digital / Tech" },
  { id: "niche", label: "Vertical" },
] as const;
